import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Button,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Badge,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  Textarea,
  useDisclosure,
  Spinner,
  InputGroup,
  InputLeftElement,
  Icon,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2, FiPlus, FiSearch, FiTag } from 'react-icons/fi';

const API_BASE = '/api/v1/admin-ui';

function ProfileTags() {
  const toast = useToast();
  
  // State
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingTag, setEditingTag] = useState(null);
  const [tagForm, setTagForm] = useState({ name: '', description: '' });
  const [tagToDelete, setTagToDelete] = useState(null);
  
  const { isOpen: isTagModalOpen, onOpen: onTagModalOpen, onClose: onTagModalClose } = useDisclosure();
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
    'X-Tenant-ID': localStorage.getItem('instance_id'),
  });

  // Fetch tags
  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/profile-tags/`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        setTags(data.tags || []);
      } else {
        toast({ title: data.message || 'Failed to load tags', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error loading tags', status: 'error', duration: 3000 });
    }
    setLoading(false);
  };

  // Filter tags by search term
  const filteredTags = tags.filter(tag => 
    (tag.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (tag.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Tag Modal Handlers
  const openAddTag = () => {
    setEditingTag(null);
    setTagForm({ name: '', description: '' });
    onTagModalOpen();
  };

  const openEditTag = (tag) => {
    setEditingTag(tag);
    setTagForm({ 
      name: tag.name || '', 
      description: tag.description || '',
    });
    onTagModalOpen();
  };

  const handleSaveTag = async () => {
    if (!tagForm.name.trim()) {
      toast({ title: 'Tag name is required', status: 'error', duration: 3000 });
      return;
    }

    const method = editingTag ? 'PUT' : 'POST';
    const url = editingTag ? `${API_BASE}/profile-tags/${editingTag.id}/` : `${API_BASE}/profile-tags/`;
    
    try {
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(tagForm),
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: editingTag ? 'Tag updated' : 'Tag created', status: 'success', duration: 3000 });
        onTagModalClose();
        fetchTags();
      } else {
        toast({ title: data.message || 'Error saving tag', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error saving tag', status: 'error', duration: 3000 });
    }
  };

  // Delete tag
  const confirmDeleteTag = (tag) => {
    setTagToDelete(tag);
    onDeleteModalOpen();
  };

  const handleDeleteTag = async () => {
    if (!tagToDelete) return;
    try {
      const res = await fetch(`${API_BASE}/profile-tags/${tagToDelete.id}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: 'Tag deleted', status: 'success', duration: 3000 });
        fetchTags();
      } else {
        toast({ title: data.message || 'Error deleting tag', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error deleting tag', status: 'error', duration: 3000 });
    }
    onDeleteModalClose();
    setTagToDelete(null);
  };

  if (loading) {
    return (
      <Box textAlign="center" py={20}>
        <Spinner size="xl" color="orange.500" />
        <Text mt={4}>Loading profile tags...</Text>
      </Box>
    );
  }

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">Profile Tags</Heading>
        <Button leftIcon={<FiPlus />} colorScheme="orange" onClick={openAddTag}>
          Add Tag
        </Button>
      </HStack>

      <Text color="gray.600" mb={6}>
        Profile tags are used to categorize and organize users within your instance.
      </Text>

      {/* Search */}
      <InputGroup mb={6} maxW="400px">
        <InputLeftElement pointerEvents="none">
          <Icon as={FiSearch} color="gray.400" />
        </InputLeftElement>
        <Input
          placeholder="Search tags..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          focusBorderColor="orange.500"
        />
      </InputGroup>

      {/* Tags Table */}
      <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
        <Table>
          <Thead bg="gray.50">
            <Tr>
              <Th>Name</Th>
              <Th>Description</Th>
              <Th>Profiles</Th>
              <Th width="120px">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredTags.map(tag => (
              <Tr key={tag.id}>
                <Td>
                  <HStack>
                    <Icon as={FiTag} color="orange.500" />
                    <Text fontWeight="medium">{tag.name}</Text>
                  </HStack>
                </Td>
                <Td color="gray.600">{tag.description || '-'}</Td>
                <Td>
                  <Badge colorScheme="gray">{tag.profile_count || 0} profiles</Badge>
                </Td>
                <Td>
                  <HStack spacing={2}>
                    <IconButton
                      icon={<FiEdit2 />}
                      size="sm"
                      variant="ghost"
                      colorScheme="blue"
                      onClick={() => openEditTag(tag)}
                      aria-label="Edit tag"
                    />
                    <IconButton
                      icon={<FiTrash2 />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => confirmDeleteTag(tag)}
                      aria-label="Delete tag"
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
            {filteredTags.length === 0 && (
              <Tr>
                <Td colSpan={4} textAlign="center" py={8}>
                  <VStack spacing={2}>
                    <Icon as={FiTag} boxSize={8} color="gray.300" />
                    <Text color="gray.500">No profile tags found</Text>
                    <Button size="sm" colorScheme="orange" onClick={openAddTag}>
                      Create your first tag
                    </Button>
                  </VStack>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Tag Modal */}
      <Modal isOpen={isTagModalOpen} onClose={onTagModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingTag ? 'Edit Tag' : 'Add Tag'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Name</FormLabel>
                <Input
                  value={tagForm.name}
                  onChange={(e) => setTagForm({ ...tagForm, name: e.target.value })}
                  placeholder="e.g., VIP, Partner, Lead"
                  focusBorderColor="orange.500"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={tagForm.description}
                  onChange={(e) => setTagForm({ ...tagForm, description: e.target.value })}
                  placeholder="Describe what this tag is used for..."
                  focusBorderColor="orange.500"
                  rows={3}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onTagModalClose}>Cancel</Button>
            <Button colorScheme="orange" onClick={handleSaveTag}>
              {editingTag ? 'Update' : 'Create'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Tag</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              Are you sure you want to delete the tag <strong>{tagToDelete?.name}</strong>? 
              This will remove the tag from all profiles that have it.
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteModalClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDeleteTag}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default ProfileTags;
